import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { MdbFormsModule, MdbModalRef, MdbRippleModule } from 'mdb5-adapted';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LmsModalFrameComponent } from '@lmsSharedUI';

import { RejectReasonCodesService } from '../reject-reason-codes/reject-reason-codes.service';

export declare type RejectType = 'Cancellation' | 'Rejection';

export interface ReasonCode {
  reasonCode: string;
  description: string;
}

export interface RejectOfferInfo {
  quoteId?: string | number;
  rejectOfferReason?: string | string[];
  loanStatus?: string;
  requiresRejectionLetter?: boolean | string;
}

@Component({
  selector: 'app-reject-offer',
  templateUrl: './reject-offer.component.html',
  styleUrls: ['./reject-offer.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MdbFormsModule, MdbRippleModule, LmsModalFrameComponent],
})
export class RejectOfferComponent implements OnInit, OnDestroy {
  @Input() lstReasonCode: ReasonCode[] = [];
  @Input() required: boolean = false;
  @Output() selectionChange = new EventEmitter<ReasonCode[]>();
  @Output() onClose = new EventEmitter<RejectOfferInfo | null>();

  selectedReasons: ReasonCode[] = [];
  showValidationError: boolean = false;
  rejectOfferReasonDetailsForm: FormGroup;
  submitted = false;

  rejectOfferInfo?: RejectOfferInfo;
  cancelOrReject?: RejectType;

  private destroy$ = new Subject<void>();

  // modalRef will be injected when opened as a modal; make it optional to avoid runtime errors
  constructor(
     public modalRef: MdbModalRef<RejectOfferComponent>,
    private formBuilder: FormBuilder,
    public reasonCodesService: RejectReasonCodesService
  ) {}

  get rejectOfferReason() {
    return this.rejectOfferReasonDetailsForm.get('rejectOfferReason');
  }

  get requiresRejectionLetter() {
    return this.rejectOfferReasonDetailsForm.get('requiresRejectionLetter');
  }

  getSelectedReasonCodes(): string[] {
    return this.selectedReasons.map(r => r.reasonCode);
  }

  isSelected(reason: ReasonCode): boolean {
    return this.selectedReasons.some(r => r.reasonCode === reason.reasonCode);
  }

  onOptionChange(event: any, reason: ReasonCode) {
    if (event.target.checked) {
      this.selectedReasons.push(reason);
    } else {
      this.selectedReasons = this.selectedReasons.filter(
        r => r.reasonCode !== reason.reasonCode
      );
    }

    // Update the form control value with selected reason codes
    this.updateFormControlValue();
    this.selectionChange.emit(this.selectedReasons);
    this.validateSelection();
  }

  // Update the form control with selected reason codes
  private updateFormControlValue(): void {
    const selectedCodes = this.getSelectedReasonCodes();
    this.rejectOfferReasonDetailsForm.patchValue({
      rejectOfferReason: selectedCodes.length > 0 ? selectedCodes : null
    });

    // Manually update validity
    if (selectedCodes.length > 0) {
      this.rejectOfferReasonDetailsForm.get('rejectOfferReason')?.setErrors(null);
    } else {
      this.rejectOfferReasonDetailsForm.get('rejectOfferReason')?.setErrors({ required: true });
    }
  }

  validateSelection(): void {
    if (this.required) {
      this.showValidationError = this.selectedReasons.length === 0;
    } else {
      this.showValidationError = false;
    }
  }

  // Method to clear selection
  clearSelection(): void {
    this.selectedReasons = [];
    this.showValidationError = false;
    this.rejectOfferReasonDetailsForm.patchValue({
      requiresRejectionLetter: 'No'
    });
    this.updateFormControlValue();
    this.selectionChange.emit(this.selectedReasons);
  }

  ngOnInit(): void {
    // Initialize form first
    this.rejectOfferReasonDetailsForm = this.formBuilder.group({
      id: [this.rejectOfferInfo?.quoteId],
      rejectOfferReason: [null, [Validators.required]],
      requiresRejectionLetter: ['', [Validators.required]], // Default to 'No'
    });

    // Load rejection reasons from service
    this.reasonCodesService
      .getAllRejectReasonByReasonType(this.cancelOrReject || 'Rejection')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.lstReasonCode = res;

        // Initialize with any previously selected values if they exist
        if (this.rejectOfferInfo?.rejectOfferReason) {
          this.initializeSelectedReasons();
        }
      });
  }

  // Initialize selected reasons from existing data
  private initializeSelectedReasons(): void {
    if (!this.rejectOfferInfo) return;

    const existingReasons = this.rejectOfferInfo.rejectOfferReason;
    if (existingReasons) {
      // Handle both string (single reason) and array (multiple reasons)
      const reasonCodes = Array.isArray(existingReasons) ? existingReasons : [existingReasons];

      reasonCodes.forEach(code => {
        const reason = this.lstReasonCode.find(r => r.reasonCode === code);
        if (reason) {
          this.selectedReasons.push(reason);
        }
      });

      this.updateFormControlValue();
    }
  }

  saveRejectOfferReason() {
    this.submitted = true;

    // Ensure form validation is current
    this.updateFormControlValue();

    // Early exit if form is invalid
    if (!this.rejectOfferReasonDetailsForm.valid) {
      return;
    }

    // Prepare data for saving
    this.rejectOfferInfo = this.rejectOfferInfo || {};
    this.rejectOfferInfo.quoteId = this.rejectOfferReasonDetailsForm.get('id')?.value;
    this.rejectOfferInfo.rejectOfferReason = this.getSelectedReasonCodes();

    // Convert string 'yes'/'no' to boolean for requiresRejectionLetter
    const letterValue = this.rejectOfferReasonDetailsForm.get('requiresRejectionLetter')?.value;
    this.rejectOfferInfo.requiresRejectionLetter = letterValue === 'Yes';

    // Safely close modal if available, otherwise emit onClose for host handling
    if (this.modalRef) {
      this.modalRef.close(this.rejectOfferInfo);
    } else {
      this.onClose.emit(this.rejectOfferInfo);
    }
  }

  close() {
    if (this.modalRef) {
      this.modalRef.close();
    } else {
      this.onClose.emit(null);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
